import { encode, useFormatAmount, useIntl } from '@capsicum/common';
import {
	Alert,
	AmountSelection,
	Button,
	ButtonSection,
	TextLineOrSkeleton,
	Tooltip,
	useRouter
} from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import { useWithdrawDataAtom, useWithdrawState } from '@capsicum/features/wallet/shared';
import { useEffect, useState } from 'react';
import { type FieldError } from 'react-hook-form';
import styled from 'styled-components';

export const AmountFT = () => {
	const { $t } = useIntl();
	const { push } = useRouter();
	const { formatAmount } = useFormatAmount();
	const { fanToken, setCurrencyId, setInputAmount } = useWithdrawDataAtom();
	const [error, setError] = useState<FieldError | undefined>(undefined);

	const {
		altCurrency,
		conversionAltPrice,
		conversionRates,
		convertedDefaultPrice,
		inputValue,
		isWithdrawLoading,
		minFT,
		onAmountChange
	} = useWithdrawState(fanToken?.id, fanToken?.isExternalToken);

	const balanceConvertedPrice = formatAmount((fanToken?.amount || 0) * Number(conversionRates?.[0]?.rate), {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2
	});

	const onButtonNextClick = () => {
		setCurrencyId(fanToken?.id || '');
		setInputAmount(inputValue?.toString());
		push(`/wallet/withdraw/recipients`);
	};

	const isValueOutsideLimits =
		!inputValue || !minFT || Number(inputValue) < Number(minFT) || Number(inputValue) > (fanToken?.amount || 0);

	useEffect(() => {
		if (isValueOutsideLimits) {
			setError({
				message: $t('wallet.withdraw.amountSelection.amountErrorFT', {
					maxValue: Number(fanToken?.amount),
					minValue: Number(minFT),
					shortCode: fanToken?.shortCode
				}),
				type: 'error'
			});
		} else {
			setError(undefined);
		}
	}, [isValueOutsideLimits]);

	return (
		<S.Container>
			<S.PageTitle>
				{$t('wallet.withdraw.amountSelection.titleFT', { shortCode: fanToken?.shortCode })}
			</S.PageTitle>
			<S.BalanceText>
				{$t('wallet.withdraw.amountSelection.balance')}
				<S.HighlightedAmount>{fanToken?.formattedAmount}</S.HighlightedAmount>
				{!fanToken?.isExternalToken && (
					<>
						{encode('doubleTilde')}
						<TextLineOrSkeleton isLoading={isWithdrawLoading} lineWidth={60}>
							{`${balanceConvertedPrice} ${altCurrency?.shortCode}`}
						</TextLineOrSkeleton>
					</>
				)}
				<Tooltip contentPosition="bottom_right">
					<Tooltip.Trigger testID="withdrawFT-amount-subtitleTooltip" />
					<Tooltip.Content>
						<Tooltip.Description>{$t('wallet.withdraw.amountSelection.tooltipInfoFT')}</Tooltip.Description>
					</Tooltip.Content>
				</Tooltip>
			</S.BalanceText>
			<S.AmountSelectionContainer>
				<AmountSelection
					altCurrencyAmount={!fanToken?.isExternalToken ? conversionAltPrice : undefined}
					conversionPrice={!fanToken?.isExternalToken ? convertedDefaultPrice : undefined}
					currencyLogo={fanToken?.url as string}
					currencyShortName={fanToken?.shortCode}
					decimalDetail={fanToken?.isExternalToken ? 2 : 0}
					defaultCurrencyAmount={!fanToken?.isExternalToken ? `1 ${fanToken?.shortCode}` : undefined}
					error={error}
					isConversionRatesLoading={isWithdrawLoading}
					max={fanToken?.amount}
					min={Number(formatAmount(minFT))}
					testID="withdrawFT-amount-input"
					value={inputValue.toString()}
					onChange={(amount: string) => onAmountChange(amount)}
				/>
			</S.AmountSelectionContainer>
			<S.AlertWrapper
				closable={false}
				icon={InfoIcon}
				subTitle={$t('wallet.withdraw.amountSelection.alertText')}
				testID="withdrawFT-amount-warning"
				title={$t('wallet.withdraw.amountSelection.alertTitle')}
				type="message"
			/>
			<ButtonSection>
				<Button
					color="brand"
					disabled={isWithdrawLoading || isValueOutsideLimits}
					isLoading={isWithdrawLoading}
					testID="withdrawFT-amount-submitButton"
					text={$t('wallet.withdraw.amountSelection.submitButton')}
					variant="default"
					onClick={onButtonNextClick}
				/>
			</ButtonSection>
		</S.Container>
	);
};

namespace S {
	export const Container = styled.section`
		margin-top: ${({ theme }) => theme.spacings.xxlarge};
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		flex-flow: column;
		width: 100%;
		align-items: center;
		padding: ${({ theme }) => `0 ${theme.spacings.xsmall}`};
		${({ theme }) => theme.breakpoints.queries.only.tabletSmallDesktop} {
			margin: ${({ theme }) => `${theme.spacings.xxlarge} ${theme.spacings.xxxxlarge}`};
		}
	`;

	export const PageTitle = styled.span`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: left;
		width: 100%;
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const BalanceText = styled.div`
		display: flex;
		align-items: center;
		${({ theme }) => theme.fonts.text.text3}
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: left;
		width: 100%;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const HighlightedAmount = styled.span`
		${({ theme }) => theme.fonts.text.text1}
		text-align: left;
	`;
	export const AmountSelectionContainer = styled.div`
		width: 100%;
	`;

	export const AlertWrapper = styled(Alert)`
		width: 100%;
	`;
}
